import { Turbo } from '@hotwired/turbo-rails';
import ApplicationController from './application_controller';
import Logger from '../lib/logger';
import { runbookBlock, runbookToolbar } from '../lib/runbook_block';
// The button's words, in the language of the guide panel it sits in (runbook_language_controller marks each
// panel with its `lang`). `device` is the line's device as the guide marked it — 'cpe' or the hub identity —
// from the line's `data-run-device` (TopologyHelper#device_run_attributes).
const LABELS = {
    en: {
        run: (device) => (device === 'cpe' ? 'Run on the CPE' : `Run on ${device}`),
        rerun: 'Run again',
        failed: 'The request did not reach the server — try again.',
        elsewhere: (device) => `↳ not run from here: these lines run on ${device}, which the app cannot reach — run them there`,
        byHand: (device) => `↳ not run from here: the app sends ${device} only lines that are complete and read-only — these look a value up on it, or need one typed in. Paste them on ${device}`,
        runStep: (count) => `Run the ${count} CPE lines`
    },
    'pt-BR': {
        run: (device) => (device === 'cpe' ? 'Executar na CPE' : `Executar em ${device}`),
        rerun: 'Executar de novo',
        failed: 'A solicitação não chegou ao servidor — tente novamente.',
        elsewhere: (device) => `↳ não executadas daqui: estas linhas rodam em ${device}, que o app não alcança — execute-as lá`,
        byHand: (device) => `↳ não executadas daqui: o app só envia a ${device} linhas completas e somente leitura — estas consultam um valor nele, ou precisam de um digitado. Cole-as em ${device}`,
        runStep: (count) => `Executar as ${count} linhas da CPE`
    }
};
// Puts a Run button on each Troubleshooting Guide line the server marked as runnable — on the CPE, or on a hub
// the app reaches (TopologyHelper#guide_with_device_runs), the line's `data-run-device` naming which — and shows
// the line's output right under the group of lines it belongs to:
// the block is split there, so in a long step ("L2TP packets may be larger than the WAN carries", thirteen lines in
// sub-steps a) to e)) each result sits by its own commands rather than at the end of the block. A group is a run of
// command lines; a comment, a location marker or a blank line starts the next one.
//
// A marked line carries `data-run-url` — the Run request, naming the line's position in the guide and a digest
// of it, never its text — and `data-run-output`, the id the result is streamed into (RunDeviceCommandJob).
// Nothing is marked for anyone without device access, so for them this does nothing.
//
// The button holds only an icon, so it adds no text to the line: runbook_copy_controller copies the lines'
// textContent. The two language panels mark the same positions, so a line's output box is moved to the panel
// it was last run from rather than duplicated — its id must stay unique for the stream to find it.
//
// Commands under a hub's location marker ("# on BRSAORT06 — …") get a button where the server marked them — the
// app reaches that hub (data-run-device names it) — and a line under the marker says so where it did not: a step
// whose answer is read on the hub must not look answered by running its CPE lines. On a hub the app does reach, a
// command with no button is one the app may not send — the WAN-fit step's reverse pings, which look the caller-id
// up on the hub — and the note above it says that, not that the hub is out of reach.
//
// A line with a recent result (`data-run-last`) gets it back when the page opens: its box is made in the panel being
// read, and the results are loaded in one request (`data-run-results-url`, LastRunResults) — reading the run log,
// not the device. Each box carries a Run again beside its command.
//
// The notice's Diagnose button reads the whole circuit at once: the lines marked `data-run-diagnose` are the ones a
// diagnosis runs, one session per device, and each answer arrives in its line's box as a single Run's does.
export default class extends ApplicationController {
    constructor() {
        super(...arguments);
        this.stepBlocks = new WeakMap();
        this.rerunLines = new WeakMap();
    }
    connect() {
        this.element.querySelectorAll('.rb-cmd[data-run-url]').forEach((line) => this.decorate(line));
        this.element
            .querySelectorAll('pre.runbook-commands[data-run-step-url]')
            .forEach((pre) => this.decorateStep(pre));
        this.element.querySelectorAll('pre.runbook-commands').forEach((pre) => this.noteOtherDevices(pre));
        void this.loadLastResults();
    }
    async rerun(event) {
        const button = event.currentTarget;
        const line = this.rerunLines.get(button);
        const url = line?.dataset.runUrl;
        if (!line || !url)
            return;
        this.reveal(this.outputFor(line));
        await this.post(url, button, line, line.dataset.runOutput ?? '');
    }
    async loadLastResults() {
        const url = this.element.querySelector('[data-run-results-url]')?.dataset.runResultsUrl;
        const lines = Array.from(this.element.querySelectorAll('.rb-cmd[data-run-last]')).filter((line) => !line.closest('[lang]')?.hidden);
        if (!url || lines.length === 0)
            return;
        lines.forEach((line) => this.outputFor(line));
        try {
            const response = await this.safeFetch(url, {
                credentials: 'same-origin',
                headers: { Accept: 'text/vnd.turbo-stream.html' }
            });
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`);
            Turbo.renderStreamMessage(await response.text());
        }
        catch (error) {
            Logger.warn('Could not load the last results of the guide lines', { controller: this.constructor.name, url, error });
        }
    }
    // A step with two or more runnable lines runs them all in one SSH session (RunStepJob). The lines' output boxes
    // are made first, so each line's result has a place to land; the step's summary lands under the block.
    async runStep(event) {
        const button = event.currentTarget;
        // Held by reference: the block's lines may since have been split across several pres.
        const pre = this.stepBlocks.get(button);
        const url = pre?.dataset.runStepUrl;
        if (!pre || !url)
            return;
        Array.from(runbookBlock(pre).querySelectorAll('pre.runbook-commands .rb-cmd[data-run-url]'))
            .filter(onTheCpe)
            .forEach((line) => this.outputFor(line));
        this.reveal(this.stepStatusFor(pre));
        await this.post(url, button, pre, pre.dataset.runStepOutput ?? '');
    }
    // A diagnosis reads the circuit end to end (DiagnoseDeviceJob): the lines the server marked as its own
    // (`data-run-diagnose`) each get their output box in the panel being read before the request leaves, so every
    // answer has its place under its own line; the summary lands in the notice, in a box the server draws.
    //
    // The server says whether one started (`X-Diagnosis`): refused — another is under way, or it is too soon — or
    // failed on the way, the boxes just made would stay empty under every line, so they are taken away again, and a
    // request that never arrived says so where the summary would be.
    async diagnose(event) {
        const button = event.currentTarget;
        const url = button.dataset.runDiagnoseUrl;
        if (!url)
            return;
        const panel = button.closest('[lang]') ?? document;
        const made = Array.from(panel.querySelectorAll('.rb-cmd[data-run-diagnose]'))
            .filter((line) => !document.getElementById(line.dataset.runOutput ?? ''))
            .map((line) => this.outputFor(line));
        button.disabled = true;
        let started = false;
        try {
            const response = await this.send(url);
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`);
            started = response.headers?.get('X-Diagnosis') === 'started';
            Turbo.renderStreamMessage(await response.text());
        }
        catch (error) {
            Logger.error('Failed to start the diagnosis', error, { controller: this.constructor.name, url });
            const summary = panel.querySelector('.circuit-diagnosis');
            if (summary)
                summary.textContent = this.labels(button).failed;
        }
        finally {
            button.disabled = false;
        }
        if (!started)
            made.forEach((output) => (output.closest('.runbook-run-output') ?? output).remove());
    }
    async run(event) {
        const button = event.currentTarget;
        const line = button.closest('.rb-cmd[data-run-url]');
        const url = line?.dataset.runUrl;
        if (!line || !url)
            return;
        this.reveal(this.outputFor(line));
        await this.post(url, button, line, line.dataset.runOutput ?? '');
    }
    async post(url, button, near, outputId) {
        button.disabled = true;
        try {
            const response = await this.send(url);
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`);
            Turbo.renderStreamMessage(await response.text());
        }
        catch (error) {
            Logger.error('Failed to run the guide line on the device', error, { controller: this.constructor.name, url });
            const output = outputId ? document.getElementById(outputId) : null;
            if (output)
                output.textContent = this.labels(near).failed;
        }
        finally {
            button.disabled = false;
        }
    }
    // One POST, answered with Turbo streams. No retry: one that reached the server but lost its response would run
    // the line — or start the diagnosis — twice.
    send(url) {
        return this.safeFetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'text/vnd.turbo-stream.html', 'X-CSRF-Token': this.csrfToken() }
        }, 0);
    }
    // The step's Run button, at the left of the block's toolbar (Copy commands is at the right).
    decorateStep(pre) {
        const toolbar = runbookToolbar(pre);
        if (toolbar.querySelector('.runbook-run-step'))
            return;
        const count = Array.from(pre.querySelectorAll('.rb-cmd[data-run-url]')).filter(onTheCpe).length;
        // One line has its own button, and none is nothing to run: no "Run the 0 CPE lines".
        if (count < 2)
            return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-sm btn-outline-primary runbook-run-step';
        button.dataset.action = 'runbook-run#runStep';
        const icon = document.createElement('i');
        icon.className = 'fas fa-forward me-1';
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon, this.labels(pre).runStep(count));
        this.stepBlocks.set(button, pre);
        toolbar.prepend(button);
    }
    // Under each location marker that is not the CPE's, in a block with a line the app cannot run: that command has
    // no button because it runs on a device the app cannot reach — a hub whose access is off or has no stored row —
    // not because something is wrong with it. On a hub the app does reach — one a line of this guide is marked for —
    // the command is one the app may not send, and the note sits right above it: every hub section of the WAN-fit
    // step is a session line with a button and two pings without, which read as "only the first one runs" under a
    // note that said the hub could not be reached (CLBR.7321.E003, 2026-10-12).
    noteOtherDevices(pre) {
        if (!pre.querySelector('.rb-cmd[data-run-url]') || pre.querySelector('.rb-elsewhere'))
            return;
        // The whole guide, not this block: a hub is reached where any of the guide's lines is marked for it.
        const guide = pre.closest('[data-controller~="runbook-run"]') ?? document;
        const reached = new Set(Array.from(guide.querySelectorAll('.rb-cmd[data-run-device]')).map((line) => (line.dataset.runDevice ?? '').toUpperCase()));
        let marker = null;
        pre.querySelectorAll(':scope > .rb-line').forEach((line) => {
            if (line.classList.contains('rb-where')) {
                marker = CPE_MARKER.test(line.textContent ?? '') ? null : line;
            }
            else if (marker && line.classList.contains('rb-cmd') && !line.dataset.runUrl) {
                const device = markerDevice(marker);
                const note = document.createElement('span');
                note.className = 'rb-line rb-elsewhere';
                if (reached.has(device)) {
                    note.textContent = this.labels(pre).byHand(device);
                    line.before(note);
                }
                else {
                    note.textContent = this.labels(pre).elsewhere(device);
                    marker.after(note);
                }
                marker = null;
            }
        });
    }
    decorate(line) {
        if (line.querySelector('.runbook-run'))
            return;
        const device = line.dataset.runDevice || 'cpe';
        const label = `${this.labels(line).run(device)}: ${line.textContent ?? ''}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-sm btn-outline-primary runbook-run';
        button.title = this.labels(line).run(device);
        button.setAttribute('aria-label', label);
        button.dataset.action = 'runbook-run#run';
        const icon = document.createElement('i');
        icon.className = 'fas fa-play';
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon);
        line.prepend(button);
    }
    // The box a line's result is streamed into, labelled with the line's command — quietly, on one line, since the
    // command is already in the block and the label only says which line this is.
    outputFor(line) {
        const id = line.dataset.runOutput ?? '';
        const list = this.outputsFor(line);
        const existing = id ? document.getElementById(id) : null;
        if (existing) {
            const item = existing.closest('.runbook-run-output') ?? existing;
            if (item.parentElement !== list)
                list.append(item);
            return existing;
        }
        const text = line.textContent ?? '';
        const item = document.createElement('div');
        item.className = 'runbook-run-output';
        const head = document.createElement('div');
        head.className = 'runbook-run-output-head';
        const command = document.createElement('span');
        command.className = 'runbook-run-output-command';
        command.title = text;
        command.textContent = text;
        const again = document.createElement('button');
        again.type = 'button';
        again.className = 'btn btn-sm btn-outline-secondary runbook-run-rerun';
        again.title = this.labels(line).rerun;
        again.setAttribute('aria-label', `${this.labels(line).rerun}: ${text}`);
        again.dataset.action = 'runbook-run#rerun';
        const icon = document.createElement('i');
        icon.className = 'fas fa-redo';
        icon.setAttribute('aria-hidden', 'true');
        again.append(icon);
        this.rerunLines.set(again, line);
        head.append(command, again);
        const output = document.createElement('div');
        output.id = id;
        output.setAttribute('aria-live', 'polite');
        item.append(head, output);
        list.append(item);
        return output;
    }
    // Where a step run's summary lands: under the block, whose lines' results are inside it.
    stepStatusFor(pre) {
        const id = pre.dataset.runStepOutput;
        if (!id)
            return null;
        const block = runbookBlock(pre);
        let status = document.getElementById(id);
        if (!status) {
            status = document.createElement('div');
            status.id = id;
            status.className = 'runbook-run-step-status';
            status.setAttribute('aria-live', 'polite');
        }
        if (block.nextElementSibling !== status)
            block.after(status);
        return status;
    }
    // The results of a line's group, right under it. A group that ends before its pre does splits the pre there: the
    // lines after it move to a new pre in the same block, and the results sit between the two.
    outputsFor(line) {
        const pre = line.closest('pre');
        if (!pre)
            return this.outputsAfter(line);
        runbookBlock(pre);
        let end = line;
        while (end.nextElementSibling?.classList.contains('rb-cmd'))
            end = end.nextElementSibling;
        if (end.nextElementSibling) {
            const rest = document.createElement('pre');
            rest.className = 'runbook-commands runbook-commands-rest';
            while (end.nextElementSibling)
                rest.append(end.nextElementSibling);
            pre.after(rest);
        }
        return this.outputsAfter(pre);
    }
    outputsAfter(element) {
        const next = element.nextElementSibling;
        if (next instanceof HTMLElement && next.classList.contains('runbook-run-outputs'))
            return next;
        const list = document.createElement('div');
        list.className = 'runbook-run-outputs';
        element.after(list);
        return list;
    }
    // A step's summary lands under its block, which can be below the fold; bring the box into view when a run starts.
    reveal(element) {
        element?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
    csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content ?? '';
    }
    labels(element) {
        return element.closest('[lang]')?.getAttribute('lang') === 'pt-BR' ? LABELS['pt-BR'] : LABELS.en;
    }
}
// A step run is the CPE's lines only (RunbookCommands#step): a hub's lines in the same block are run one by one,
// so the step's button does not count them and its run makes no box for them — with the WAN-fit step's hub pings
// runnable, its button read "Run the 22 CPE lines" over 13.
function onTheCpe(line) {
    return (line.dataset.runDevice || 'cpe') === 'cpe';
}
// The location marker for the CPE, as RunbookCommands reads it on the server.
const CPE_MARKER = /^#\s*(?:on|no|em)\s+(?:the\s+)?CPE\b/i;
// A hub's location marker ("# on BRSAORT06 — …"): the first word after "on/no/em [the]" is the hub's identity, as
// the guide prints it.
const HUB_MARKER = /^#\s*(?:on|no|em)\s+(?:the\s+)?([A-Za-z0-9][A-Za-z0-9.]*)/;
function markerDevice(marker) {
    return (HUB_MARKER.exec(marker.textContent ?? '')?.[1] ?? '').toUpperCase() || 'another device';
}
